import { FC, Fragment, useState } from 'react';

import {
	IAiResultFields,
	INormalizedAiResult,
	PHOTO_INDEX_REGEX,
	normalizeAiResult,
} from '../../../utils/aiResults';
import { useAiReportContext } from '../../table/userSingleReport/aiReportContext';

import styles from './aiResultCell.module.scss';

const COMMENT_PREVIEW_LENGTH = 120;

const getResultModifier = (result: string) => {
	const normalized = result.toLowerCase();

	if (
		normalized.includes('не подтвержд') ||
		normalized.includes('не выполнен') ||
		normalized.includes('не выполнено') ||
		normalized.includes('unconfirmed') ||
		normalized.includes('failed')
	) {
		return styles.result_negative;
	}

	if (
		normalized.includes('выполнен') ||
		normalized === 'done' ||
		normalized === 'pass' ||
		normalized === 'passed' ||
		normalized.includes('completed') ||
		normalized.includes('success')
	) {
		return styles.result_positive;
	}

	return styles.result_neutral;
};

interface IAiResultCellProps {
	item?: IAiResultFields | INormalizedAiResult | null;
	result?: string | null;
	comment?: string | null;
	onPhotoIndexClick?: (index: number) => void;
}

const renderCommentWithPhotoLinks = (
	comment: string,
	onPhotoIndexClick?: (index: number) => void
) => {
	if (!onPhotoIndexClick) {
		return comment;
	}

	const parts: Array<string | JSX.Element> = [];
	let lastIndex = 0;
	let match: RegExpExecArray | null;
	const regex = new RegExp(PHOTO_INDEX_REGEX.source, 'g');

	while ((match = regex.exec(comment)) !== null) {
		if (match.index > lastIndex) {
			parts.push(comment.slice(lastIndex, match.index));
		}
		const photoIndex = Number(match[1]);
		parts.push(
			<button
				key={`${match.index}-${photoIndex}`}
				type='button'
				className={styles.photoLink}
				onClick={() => onPhotoIndexClick(photoIndex)}
			>
				[{photoIndex}]
			</button>
		);
		lastIndex = match.index + match[0].length;
	}

	if (lastIndex < comment.length) {
		parts.push(comment.slice(lastIndex));
	}

	return parts.map((part, index) => (
		<Fragment key={index}>{part}</Fragment>
	));
};

const AiResultCell: FC<IAiResultCellProps> = ({
	item,
	result,
	comment,
	onPhotoIndexClick,
}) => {
	const { onPhotoIndexClick: contextPhotoClick } = useAiReportContext();
	const handlePhotoIndexClick = onPhotoIndexClick || contextPhotoClick;
	const [isExpanded, setIsExpanded] = useState(false);
	const ai =
		normalizeAiResult(item) ||
		normalizeAiResult(
			result || comment
				? {
						result: result || undefined,
						reason: comment || undefined,
				  }
				: null
		);

	if (!ai) {
		return <span>—</span>;
	}

	const needsExpand = ai.comment.length > COMMENT_PREVIEW_LENGTH;
	const visibleComment =
		needsExpand && !isExpanded
			? `${ai.comment.slice(0, COMMENT_PREVIEW_LENGTH).trim()}…`
			: ai.comment;

	return (
		<div className={styles.cell}>
			{ai.result && (
				<span className={`${styles.result} ${getResultModifier(ai.result)}`}>
					{ai.result}
				</span>
			)}
			{ai.comment && (
				<>
					<p className={styles.comment}>
						{renderCommentWithPhotoLinks(visibleComment, handlePhotoIndexClick)}
					</p>
					{needsExpand && (
						<button
							type='button'
							className={styles.expand}
							onClick={() => setIsExpanded((prev) => !prev)}
						>
							{isExpanded ? 'свернуть' : 'показать полностью'}
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default AiResultCell;
